// src/api/lifecycle.js
//
// Thin client for the proxy's server-side ad-lifecycle graph
// (GET /proxy/lifecycle — proxy-server/apiServer.js, backed by
// proxy-server/lifecycle/summarize.js -> groupLifecyclesBySlot). Mirrors the
// private request() helper in api/launches.js / api/checks.js — same
// base-URL resolution (REACT_APP_PROXY_API_URL in prod, falls back to a bare
// "/proxy" path relative to the page origin locally).
//
// WHY THIS FILE EXISTS: the per-slot QA sign-off report (utils/
// buildSignOffReport.js, triggered from InspectraNetworkView's 📋 button)
// used to recompute its own lifecycle graph in the browser
// (buildSlotLifecycles + groupLifecyclesBySlot from ./AdLifecycleView) while
// agents/CI already got the proxy's own server-side graph (the
// `lifecycle_summary` MCP tool, GET /proxy/lifecycle). Two authoritative-
// looking answers to "what happened to this ad slot" — one of them for the
// artifact a human actually SIGNS. This client lets the report consume the
// same server graph instead. Follows the house pattern LaunchPage.jsx
// established for its verdict (api/launches.js's getLaunchRollup()).
//
// GET /proxy/lifecycle?grouped=1&detail=1&full=1&limit=<n> ->
//   { rowCount, slotCount, bidNoFill (+ .items[] under detail),
//     inventoryFlow, bidHealth, bidHealthByAppPlatform (WP3a — per-app×
//     platform segmentation, buildSignOffReport.js's bidHealthSection),
//     omid, groupCount, groups: [ full groupLifecyclesBySlot() group
//       objects, each carrying its own slots[] of complete per-slot
//       lifecycle objects — the exact shape buildSignOffReport.js's
//       collectCustParams/demandWastedSection/orphanSection/contextLabel/
//       refreshNote already expect ], orphanReport (WP2 — buildOrphanReport
//       output, {orphans, duplicates, counts, reasons}), redundancies,
//       lifecycles (flat, uncapped),
//     truncated: { groups, shown } | null }
// `limit` defaults to 50 server-side if omitted — always pass an explicit
// limit here so a real session's full slot count isn't silently clamped to
// that default (see summarize.js's HARD_CAP header comment for the 2000
// ceiling this can go up to before the server itself clamps). `full`
// defaults true in getLifecycleGroups() below — see its own docstring for
// why: without it, groups/orphanReport/redundancies/lifecycles above are
// either absent or the slim groupSummary() projection instead.

const PROXY_API_BASE = (process.env.REACT_APP_PROXY_API_URL || '/proxy').replace(/\/$/, '');

/**
 * Internal: GET JSON against the proxy REST API. Always resolves the parsed
 * JSON body on 2xx. On a non-2xx response, or if the fetch itself fails
 * (proxy unreachable), rejects with an Error carrying `.status`/`.body` —
 * mirrors api/launches.js's request() exactly, including its `.status = 0`
 * convention for a network-level failure (proxy down / unreachable), which
 * the sign-off button uses to distinguish "no data" from "couldn't ask".
 */
async function request(path, init = {}) {
  let res;
  try {
    res = await fetch(`${PROXY_API_BASE}/proxy${path}`, {
      ...init,
      headers: {
        Accept: 'application/json',
        ...(init.headers || {}),
      },
    });
  } catch (e) {
    const err = new Error(`Could not reach the proxy server at ${PROXY_API_BASE}`);
    err.status = 0;
    err.body = null;
    err.cause = e;
    throw err;
  }

  let json = null;
  try { json = await res.json(); } catch { /* empty/non-JSON body */ }

  if (!res.ok) {
    const err = new Error((json && (json.detail || json.error)) || `${init.method || 'GET'} ${path} failed (${res.status})`);
    err.status = res.status;
    err.body = json;
    throw err;
  }
  return json;
}

/**
 * GET /proxy/lifecycle?grouped=1&detail=1&full=1&limit=<limit>[&windowSec=<n>]
 * -> the full server-side grouped lifecycle graph (see file header for shape).
 *
 * `limit` defaults to 2000 here — matching summarize.js's raised HARD_CAP —
 * so a real session's groups come back whole rather than silently capped at
 * the route's own 50-slot default. Pass a smaller `limit` explicitly if a
 * caller ever wants a slimmer fetch; `windowSec` scopes to only rows newer
 * than (now - windowSec), omitted meaning the full live history buffer.
 *
 * NOTE (WP-F6): the sign-off button no longer calls this — GET reads the proxy's WHOLE
 * history (all devices, earlier sessions). It POSTs the device-scoped rows via
 * postLifecycleGroups instead. Prefer the POST for anything a user can scope.
 *
 * `full` defaults to true — matching postLifecycleGroups()'s own default —
 * because this function's one caller (buildSignOffReport.js, via the
 * sign-off button in InspectraNetworkView.jsx) needs everything `full`
 * unlocks: orphanReport (WP2, frontend-validation-port-spec.md §4) plus the
 * UNPROJECTED group objects (pageName/visitLabel/sizes/legitRefreshCount/
 * rapidRefreshCount — fields buildSignOffReport.js's contextLabel()/
 * refreshNote() read directly off each group; groupSummary()'s slim 9-field
 * projection does not carry them). Before this, GET didn't parse `full` at
 * all, so orphanReport was unreachable from the sign-off report even though
 * summarize.js already computed it — see apiServer.js's GET /proxy/lifecycle
 * comment. Pass `full: false` explicitly for a smaller fetch if a future
 * caller only wants the slim per-group summary.
 *
 * Throws on any failure (network-unreachable or non-2xx) — deliberately NOT
 * swallowed to a default value, unlike getLaunch()'s 404-to-null contract:
 * a sign-off report has nothing sane to fall back to silently (see
 * buildSignOffReport.js's `lifecycleSource` provenance contract — callers of
 * this function are expected to catch and surface the failure, not paper
 * over it).
 */
export async function getLifecycleGroups({ limit = 2000, windowSec, full = true } = {}) {
  const params = new URLSearchParams();
  params.set('grouped', '1');
  params.set('detail', '1');
  if (full) params.set('full', '1');
  if (limit != null) params.set('limit', String(limit));
  if (windowSec != null) params.set('windowSec', String(windowSec));
  return request(`/lifecycle?${params.toString()}`, { method: 'GET' });
}

/**
 * POST /proxy/lifecycle -> the same grouped graph, computed over ROWS THIS
 * BROWSER POSTS rather than the proxy's live history buffer.
 *
 * Use this, not getLifecycleGroups(), anywhere the frontend's own row scope is
 * the question: the Lifecycle tab renders what the dashboard is showing, which
 * may be filtered, explicitly selected, or replayed from an imported HAR with
 * no live proxy session behind it at all. GET would silently substitute the
 * server's session for the user's scope and break HAR review outright.
 *
 * `rows` is sent as-is — the same row objects buildSlotLifecycles reads
 * locally, so the server computes over identical input (that equivalence is
 * what validation/lifecycleParity.test.js gates).
 *
 * An EMPTY array is meaningful and is sent as such: "showing no rows" must
 * render an empty graph, never fall back to the whole session. Same
 * throws-on-failure contract as getLifecycleGroups above.
 */
/**
 * Every row field the server-side lifecycle pipeline reads —
 * buildSlotLifecycles, groupLifecycles, bidNoFill, inventoryFlow, orphanReport,
 * redundancyFindings, confidence, correlationKeys.
 *
 * WHY A PROJECTION: measured against real captured traffic, posting whole rows
 * is ~943 KB per 400 rows (~3.5 MB for a full session), and that sample was
 * already body-stripped. Most of that is fields the graph never looks at. The
 * debounce in AdLifecycleView stops repeat uploads; this stops each one being
 * huge.
 *
 * DANGER, and why the list is not eyeballed: dropping a field the server reads
 * does not error — it silently changes the answer. `responseBody` is the sharp
 * example: only APS bid detection reads it, and body-stripped rows will happily
 * pass a test that omits it. So this list is derived from a static scan of the
 * server modules, and lifecycleRowFields.test.js RE-RUNS that scan and fails if
 * any field the server references is missing here. Add a field access on the
 * server and that test tells you to come back and add it here too.
 */
export const LIFECYCLE_ROW_FIELDS = [
  // identity + correlation
  '_k', 'id',
  // visit/page attribution — buildSlotLifecycles reads each as `_x ?? x`, so
  // BOTH spellings must survive the projection or slot cards lose their visit
  // labels and page URLs. Caught by lifecycleRowFields.test.js, not by eye.
  '_visitIdx', 'visitIdx', '_visitKey', 'visitKey',
  '_pageUrl', 'pageUrl', '_visitReason', 'visitReason',
  // classification
  'type', 'requestType',
  // the graph's substance
  'url', 'validData', 'issues',
  // how the row was captured ('logcat' = USB). Rides through on phases.*.row so the
  // slot story can tell a USB row (no GAM response/Prebid/APS visible) from a MITM one.
  'captureSource',
  // set by the proxy when a Chaos rule answered the request (block/mock): the
  // lifecycle reports "blocked (Chaos)" instead of reading the fake reply as 0 bids.
  'chaos',
  // timing (ts() tries these in order). `ts`/`time` are omid.js's own fallback
  // spellings (its ts() reads `row.ts ?? row.timestamp ?? row.time`): live
  // traffic rows populate `timestamp` (context/TrafficContext.js), so dropping
  // them changed nothing today, but a row sourced any other way would have
  // reached the server with no usable time and silently sorted as epoch 0 —
  // wrong OMID sequencing, no error. Two scalars; keep them.
  'startedAt', 'startedAtMs', 'startedAtISO', 'timestamp', 'timing', 'ts', 'time',
  // slot waterfall (utils/slotStory.js buildSlotTimeline): real request start + RTT
  'requestStartedAt', 'durationMs',
  // response state — APS bid detection reads the body; others read the code
  'responseBody', 'status', 'responseCode',
  // device platform, read by bidHealth.js's platformOf() for the per-app×
  // platform segmentation WP2's sign-off verdicts consume. Its `iu`-regex
  // fallback only fires for GAM rows, so without these three every Prebid and
  // APS row — which have no GAM `iu` — segments as 'Other' instead of iOS or
  // Android. Wrong numbers, no error: the exact failure this list exists to
  // prevent, and it shipped once before the scan below covered bidHealth.js.
  'platformClass', 'devicePlatform', 'platform',
  // SDK-bidding sign-off (docs/sdlc/sdk-bidding-signoff): a logcat row's URL cut at ~4KB. The
  // token walk reads it so a missing `a3p` on a cut row is `unknown_truncated`, never "missing".
  // The derived mapping record (validData.sdkBiddingMapping) and token result
  // (validData.sdkBidding.tokens) ride inside `validData`, listed above.
  'truncated',
];

/**
 * Copy of `row` carrying only LIFECYCLE_ROW_FIELDS. Absent fields stay absent
 * (rather than becoming explicit undefined) so a projected row is
 * indistinguishable from the original as far as the pipeline's `field in row`
 * and optional-chaining reads are concerned.
 */
export function projectRowForLifecycle(row) {
  if (!row || typeof row !== 'object') return row;
  const out = {};
  for (const f of LIFECYCLE_ROW_FIELDS) if (f in row) out[f] = row[f];
  return out;
}

/**
 * Rebuilds the `phases` Set on each server-computed OMID session (WP3c,
 * docs/frontend-validation-port-spec.md §4). proxy-server/lifecycle/omid.js's
 * `reconstructOmidSessions` returns `phases` as a real Set — every consumer of
 * that function's frontend counterpart (validation/omidLifecycle.js) calls
 * `.has()` on it — but `JSON.stringify(new Set())` is `{}`, so the server
 * ships it as a plain array instead (unlike the video path's generic
 * __set__-marker codec below: omidSessions has exactly one Set-valued field
 * at a known path, so there is no need for a general walker). A caller that
 * forgot this would get a session whose `phases.has(...)` throws — no error
 * at the network boundary, just a crash (or, with optional chaining, a
 * silently-false membership check) wherever a component reads it. Applied
 * unconditionally at the boundary here so every consumer of
 * postLifecycleGroups()'s `omidSessions` sees a real Set, matching
 * hydrateVideoSessions' contract for `postVideoSessions()`.
 */
export function hydrateOmidSessions(sessions) {
  if (!Array.isArray(sessions)) return sessions;
  return sessions.map((s) => (
    s && typeof s === 'object' && Array.isArray(s.phases)
      ? { ...s, phases: new Set(s.phases) }
      : s
  ));
}

export async function postLifecycleGroups(rows, {
  limit = 2000, manualOverrides, redundancyRowKeys,
  // WP1b (frontend-validation-port-spec.md): BidHealthView only needs the
  // bidNoFill/inventoryFlow rollups (always present) plus their `detail`-gated
  // items/inventoryFlowDetail — not the groups/orphanReport/redundancies/flat
  // lifecycles[] that `full` + `grouped` unlock for AdLifecycleView. Callers
  // that don't need those can pass `grouped: false, full: false` for a
  // meaningfully smaller response; AdLifecycleView's existing call sites omit
  // both and keep getting the same payload as before (additive, not a rename).
  grouped = true, full = true,
} = {}) {
  const res = await request('/lifecycle', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      rows: Array.isArray(rows) ? rows.map(projectRowForLifecycle) : [],
      grouped,
      detail: true,
      // Unprojected groups + orphanReport + redundancies + the flat
      // lifecycles[] — everything AdLifecycleView derives locally. See
      // summarize.js's `full` note.
      full,
      // The user's own slot corrections (localStorage lc_manual_overrides).
      // The server cannot know them; omitting them silently discards them.
      ...(manualOverrides ? { manualOverrides } : {}),
      // The display-filtered scope, sent as KEYS rather than a second copy of
      // the rows. `rows` above is the unfiltered set (allRows) because the
      // graph must see impression beacons the display filter hides; the
      // redundancy findings run over what the user is actually looking at.
      // Sending ~1,500 rows twice on every filter change would be megabytes;
      // keys are bytes and the server resolves them against the set it already
      // has. Omit entirely to mean "no filter".
      ...(Array.isArray(redundancyRowKeys) ? { redundancyRowKeys } : {}),
      ...(limit != null ? { limit } : {}),
    }),
  });

  // HYDRATION IS DONE HERE, NOT BY THE CALLER — same rule as postVideoSessions
  // below, and for the same reason. `omidSessions[].phases` crosses the wire as
  // an array because a Set JSON-stringifies to `{}`; AdLifecycleView's OMID
  // panel calls `s.phases.has(...)`, which throws on an array and takes the
  // whole Lifecycle tab down with it. Applied unconditionally at the boundary so
  // no consumer can ever see the wire form. Do not "optimise" this away.
  if (res && Array.isArray(res.omidSessions)) {
    res.omidSessions = hydrateOmidSessions(res.omidSessions);
  }
  return res;
}

/**
 * POST /proxy/video -> video sessions assembled by the proxy
 * (proxy-server/lifecycle/video.js), the video counterpart of
 * postLifecycleGroups.
 *
 * HYDRATION IS DONE HERE, NOT BY THE CALLER. Sessions carry `firedQuartiles` as
 * a Set on every ad break; the wire format marks those so they can be rebuilt.
 * A caller that forgot to hydrate would get a session whose quartile timeline
 * is silently empty — `.has` undefined, no error — so the codec is applied at
 * the boundary and callers only ever see real Sets. Do not "optimise" this away
 * by returning the raw JSON.
 *
 * `validate: true` asks the server to run runVideoValidation per session and
 * return `validations[]` index-aligned with `sessions[]`.
 *
 * Same throws-on-failure contract as the other clients in this file.
 */
export async function postVideoSessions(rows, { validate = false } = {}) {
  const res = await request('/video', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      rows: Array.isArray(rows) ? rows.map(projectRowForLifecycle) : [],
      ...(validate ? { validate: true } : {}),
    }),
  });
  return { ...res, sessions: hydrateVideoSessions(res && res.sessions) };
}

/**
 * Rebuilds Sets marked by the server's serializeVideoSessions. Mirror of
 * proxy-server/lifecycle/video.js's hydrateVideoSessions — kept in sync by
 * videoParity.test.js, which round-trips real sessions through both.
 */
export function hydrateVideoSessions(value) {
  if (Array.isArray(value)) return value.map(hydrateVideoSessions);
  if (value instanceof Set || value instanceof Map) return value;
  if (value && typeof value === 'object') {
    if (Array.isArray(value.__set__)) return new Set(value.__set__.map(hydrateVideoSessions));
    if (Array.isArray(value.__map__)) return new Map(value.__map__.map(([k, v]) => [k, hydrateVideoSessions(v)]));
    const out = {};
    for (const k of Object.keys(value)) out[k] = hydrateVideoSessions(value[k]);
    return out;
  }
  return value;
}
